import { forwardRef, memo, useState } from 'react';
import { ChevronDownIcon } from '@page/icons';
import { cva, type VariantProps } from 'class-variance-authority';

import { Button, buttonVariants } from '../Button';
import { Menu, MenuContent, MenuTrigger } from '../Menu';
import { cn } from '../../lib/styles';
import { PopoverContentProps } from '../Popover';

const splitButtonContainerVariants = cva('flex items-center overflow-clip rounded-sm min-w-0', {
  variants: {
    variant: {
      primary: 'gap-px',
      secondary: 'gap-px',
      tertiary: 'gap-px',
      outline: 'gap-0',
      link: 'gap-0',
    },
    size: {
      lg: '',
      md: '',
      sm: '',
    },
  },
  defaultVariants: {
    variant: 'secondary',
    size: 'md',
  },
});

const splitButtonMainVariants = cva('flex items-center gap-1 min-w-0 rounded-none rounded-l-sm truncate', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
      tertiary: '',
      outline: 'border-r-0',
      link: '',
    },
    size: {
      lg: 'px-4 h-10 font-body-md',
      md: 'px-3 h-8 font-body-sm',
      sm: 'px-2.5 h-7 font-body-sm',
    },
  },
  defaultVariants: {
    variant: 'secondary',
    size: 'md',
  },
});

const splitButtonDropdownVariants = cva('flex items-center justify-center shrink-0 rounded-none rounded-r-sm', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
      tertiary: '',
      outline: 'border-l border-border-default',
      link: '',
    },
    size: {
      lg: 'pl-1.5 pr-2 h-10',
      md: 'pl-1.5 pr-2 h-8',
      sm: 'pl-0.5 pr-1 h-7',
    },
  },
  defaultVariants: {
    variant: 'secondary',
    size: 'md',
  },
});

export interface SplitButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'color'>,
    Omit<VariantProps<typeof buttonVariants>, 'size'> {
  size?: VariantProps<typeof splitButtonMainVariants>['size'];
  leftIcon?: React.ReactNode;
  children: React.ReactNode;
  /** Content to display in the dropdown menu */
  menuContent?: React.ReactNode;
  /** Callback when the main button is clicked */
  onClick?: () => void;
  onOpenChange?: (open: boolean) => void;
  /** Whether the dropdown is controlled */
  open?: boolean;
  align?: PopoverContentProps['align'];
  withinPortal?: boolean;
  mainButtonClassName?: string;
  dropdownButtonClassName?: string;
  menuContentClassName?: string;
}

const SplitButtonBase = (props: SplitButtonProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  const {
    children,
    leftIcon,
    menuContent,
    onClick,
    onOpenChange,
    open: controlledOpen,
    variant = 'secondary',
    size = 'md',
    color = 'default',
    disabled = false,
    align = 'start',
    withinPortal = false,
    className,
    mainButtonClassName,
    dropdownButtonClassName,
    menuContentClassName,
    ...rest
  } = props;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen);
    }

    onOpenChange?.(newOpen);
  };

  const mainButtonClasses = cn(
    buttonVariants({ variant, size, color }),
    splitButtonMainVariants({ variant, size }),
    { 'cursor-pointer': !disabled, 'pointer-events-none': disabled },
    mainButtonClassName,
  );

  const dropdownButtonClasses = cn(
    buttonVariants({ variant, size, color }),
    splitButtonDropdownVariants({ variant, size }),
    { 'cursor-pointer': !disabled, 'pointer-events-none': disabled },
    dropdownButtonClassName,
  );

  const containerClasses = cn(splitButtonContainerVariants({ variant, size }), className);

  if (!menuContent) {
    return (
      <Button
        variant={variant}
        size={size}
        color={color}
        disabled={disabled}
        leftIcon={leftIcon}
        onClick={onClick}
        className={className}
      >
        {children}
      </Button>
    );
  }

  return (
    <div className={containerClasses} ref={ref}>
      <button
        type="button"
        className={cn(mainButtonClasses, 'flex-1 min-w-0')}
        onClick={onClick}
        disabled={disabled}
        data-disabled={disabled}
        {...rest}
      >
        <div data-slot="content" className="flex items-center gap-1 w-full min-w-0">
          {leftIcon && <span className="shrink-0">{leftIcon}</span>}

          <span className="truncate min-w-0">{children}</span>
        </div>
      </button>

      <Menu open={open} onOpenChange={handleOpenChange}>
        <MenuTrigger asChild>
          <button
            type="button"
            className={dropdownButtonClasses}
            disabled={disabled}
            data-disabled={disabled}
            aria-label="Open menu"
          >
            <ChevronDownIcon className="size-5" />
          </button>
        </MenuTrigger>

        <MenuContent align={align} withinPortal={withinPortal} className={menuContentClassName}>
          {menuContent}
        </MenuContent>
      </Menu>
    </div>
  );
};

export const SplitButton = memo(forwardRef(SplitButtonBase));
